import { Channel } from '@/Enums/Channel.enum';
import { geoTypeLabel } from '@/Enums/Geography/GeographyType.enum';
import type { IPaginatedApiResponse } from '@/Interfaces/ApiResponse/PaginatedApiResponse.interface';
import type {
  IAdswizzAudienceMetaRecord,
  IAudienceReferenceRecord,
  IBaseAudienceTargeting,
  IChannelAudience,
} from '@/Interfaces/Audiences/Audience.interface';
import { IAudienceControlOption, IControlOption } from '@/Interfaces/ControlOption.interface';
import type { IForecast, IForecastGeoImpression } from '@/Interfaces/Forecaster/Forecast.interface';
import type { IProduct } from '@/Interfaces/Product.interface';
import type { IAbortSignalThunkArg, IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import type { IState } from '@/Interfaces/Slices/Forecaster/State.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import {
  ADSWIZZ_AUDIENCES_ENDPOINT,
  ADSWIZZ_PRODUCTS_ENDPOINT,
  AUDIENCES_ENDPOINT,
  GEO_TARGETING_SEARCH,
  PRODUCTS_BY_CHANNELS_ENDPOINT,
} from '@/constants/endpoints';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { getControlOptionsValues } from '@/helpers/filters/filterMapping';
import { CHANNEL_ID_MAP, getUpdatedChannelValues, mapDataToLoad } from '@/helpers/forecaster';
import { getDefaultFormValues } from '@/helpers/forecaster/form';
import { splitProductsByChannel, transformProductsObject } from '@/helpers/forecaster/slice';
import { sortByLabel, sortByLabelOrName } from '@/helpers/sort';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import type { RootState } from '..';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';
import { requestMap } from './forecaster/thunks';

enum Action {
  GetForecasts = 'forecasterSlice/forecasts',
  DeleteForecast = 'forecasterSlice/deleteForecast',
}

const POST_PREVIEW_CHANNELS_ENDPOINT = '/forecaster/preview-channels';
const GET_DOWNLOAD_CSV_ENDPOINT = '/forecaster/download-csv';
const GET_GEO_IMPRESSIONS = '/forecaster/geo-impressions';
const POST_BULK_ADDRESS_CSV_ENDPOINT = '/forecaster/bulkAddressCsv';

const CUSTOM_PRODUCTS_ENDPOINT = '/forecaster/customProducts';
const FORECAST_ENDPOINT = '/forecast';

interface ISendAddressRadiusPayload {
  file: File;
  channel: Channel;
  daysInRange: number;
  frequencyCapPerDay?: number;
  selectedAudience?: IBaseAudienceTargeting<IAudienceControlOption<number>>;
  selectedProduct?: number | string;
  selectedAdswizzProduct?: number | string;
  selectedAdswizzAudience?: IBaseAudienceTargeting<IAudienceControlOption<string>>;
  doohPublishers?: { value: string }[];
  venueIds?: { id: string }[];
  mimeTypes?: string;
  dealGroups: IControlOption[] | undefined;
}
// TODO: interface

const initialState: IState = {
  products: {},
  audience: {},
  forecasts: [],
  filters: {
    page: 1,
    limit: 15,
    channel: [],
    packages: [],
    search: '',
  },
  total: 0,
  geoOptions: [],
  currentForecast: {},
  channelsData: {},
  geoImpressions: [],
  geoOptionsRequestId: null,
  productsRequestId: DEFAULT_LOADING_REQUEST_ID,
  audienceRequestId: DEFAULT_LOADING_REQUEST_ID,
  dataRequestId: DEFAULT_LOADING_REQUEST_ID,
  forecastsRequestId: DEFAULT_LOADING_REQUEST_ID,
  forecastRequestId: null,
  channelsDataRequestId: null,
  currentForecastRequestId: DEFAULT_LOADING_REQUEST_ID,
  downloadCSVRequestId: null,
  geoImpressionsRequestId: DEFAULT_LOADING_REQUEST_ID,
};

export const forecasterSlice = createSlice({
  name: 'forecaster',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    afterDeleteForecast: (state, { payload }) => {
      state.forecasts = state.forecasts.filter(({ id }: { id: any }) => id !== payload);
    },
    resetCurrentForecast: state => {
      state.currentForecast = {};
    },
  },
  extraReducers: builder => {
    requestMap.forEach(([key, thunk]) => {
      builder
        //todo: remove `as string`
        .addCase(thunk.pending, pendingCaseHandler(key as string, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key as string, []))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key as string));
    });
    builder
      .addCase(getForecastChannelsData.pending, pendingCaseHandler('channelsData'))
      .addCase(getForecastChannelsData.rejected, rejectedCaseHandler('channelsData'))
      .addCase(getForecastChannelsData.fulfilled, (state, action) => {
        if (state.channelsDataRequestId === action.meta.requestId) {
          state.channelsDataRequestId = null;
          state.channelsData = { ...state.channelsData, ...action.payload };
        }
      })
      .addCase(deleteForecastTableItem.pending, pendingCaseHandler('forecasts'))
      .addCase(deleteForecastTableItem.rejected, rejectedCaseHandler('forecasts'))
      .addCase(deleteForecastTableItem.fulfilled, (state, action) => {
        if (state.forecastsRequestId === action.meta.requestId) {
          state.forecastsRequestId = null;
          state.forecasts = action.payload;
          state.total = Math.max(0, state.total - 1);
        }
      })
      .addCase(getProducts.pending, pendingCaseHandler('products', {}))
      .addCase(getProducts.rejected, rejectedCaseHandler('products', {}))
      .addCase(getProducts.fulfilled, fulfilledCaseHandler('products'))
      .addCase(getAudience.pending, pendingCaseHandler('audience', {}))
      .addCase(getAudience.rejected, rejectedCaseHandler('audience', {}))
      .addCase(getAudience.fulfilled, fulfilledCaseHandler('audience'))
      .addCase(getForecasts.pending, pendingCaseHandler('forecasts', []))
      .addCase(getForecasts.rejected, rejectedCaseHandler('forecasts', []))
      .addCase(getForecasts.fulfilled, (state, action) => {
        if (state.forecastsRequestId === action.meta.requestId) {
          state.forecastsRequestId = null;
          state.forecasts = action.payload.data ?? [];
          state.total = action.payload.meta?.total ?? 0;
        }
      })
      .addCase(postForecast.pending, pendingCaseHandler('forecast'))
      .addCase(postForecast.rejected, rejectedCaseHandler('forecast'))
      .addCase(postForecast.fulfilled, fulfilledCaseHandler('forecast'))
      .addCase(getGeoOptions.pending, pendingCaseHandler('geoOptions', []))
      .addCase(getGeoOptions.rejected, rejectedCaseHandler('geoOptions', []))
      .addCase(getGeoOptions.fulfilled, fulfilledCaseHandler('geoOptions'))
      .addCase(downloadCSV.pending, pendingCaseHandler('downloadCSV'))
      .addCase(downloadCSV.rejected, rejectedCaseHandler('downloadCSV'))
      .addCase(downloadCSV.fulfilled, (state, action) => {
        if (state.downloadCSVRequestId === action.meta.requestId) {
          state.downloadCSVRequestId = null;
        }
      })
      .addCase(getCurrentForecast.pending, pendingCaseHandler('currentForecast'))
      .addCase(getCurrentForecast.rejected, rejectedCaseHandler('currentForecast'))
      .addCase(getCurrentForecast.fulfilled, fulfilledCaseHandler('currentForecast'))
      .addCase(sendAddressRadius.pending, pendingCaseHandler('sendAddressRadius'))
      .addCase(sendAddressRadius.rejected, rejectedCaseHandler('sendAddressRadius'))
      .addCase(sendAddressRadius.fulfilled, fulfilledCaseHandler('sendAddressRadius'))
      .addCase(getForecastGeoImpressions.pending, pendingCaseHandler('geoImpressions', []))
      .addCase(getForecastGeoImpressions.rejected, rejectedCaseHandler('geoImpressions', []))
      .addCase(getForecastGeoImpressions.fulfilled, fulfilledCaseHandler('geoImpressions'));
  },
});

export const { setFilters, afterDeleteForecast, resetCurrentForecast } = forecasterSlice.actions;

export const getForecastChannelsData = createAsyncThunk<any, any>(
  'forecasterSlice/getForecastChannelsData',
  async (payload, { rejectWithValue }) => {
    try {
      const { filteredChannels, ...restPayload } = payload ?? {};
      const apiPayload = {
        ...restPayload,
        ...(filteredChannels && {
          filteredChannels: filteredChannels.map(({ name: _name, ...rest }: { name?: string }) => rest),
        }),
      };
      const responseResult = await axios.post(POST_PREVIEW_CHANNELS_ENDPOINT, apiPayload);
      payload?.onSuccess?.();
      return getUpdatedChannelValues(filteredChannels, responseResult.data);
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

export const getProducts = createAsyncThunk<
  any,
  any,
  {
    state: RootState;
  }
>(
  'forecasterSlice/getProducts',
  async payload => {
    const productsByChannelRequest = axios.get(PRODUCTS_BY_CHANNELS_ENDPOINT, {
      ...payload,
    });
    const customProductsRequest = axios.get(CUSTOM_PRODUCTS_ENDPOINT, {
      ...payload,
    });
    const adswizzProductsRequest = axios.get(ADSWIZZ_PRODUCTS_ENDPOINT, {
      ...payload,
    });
    const responses = await Promise.all([productsByChannelRequest, customProductsRequest, adswizzProductsRequest]);

    const productsData = responses.map(({ data }) => data);
    const [productsByChannel, customProducts, adswizzProducts] = productsData;
    payload?.onSuccess?.();

    const allProductsByChannel = {
      ...transformProductsObject(productsByChannel),
      ...splitProductsByChannel(customProducts),
      [Channel.SpotifyDirect]: adswizzProducts,
    };

    return Object.fromEntries(
      Object.entries(allProductsByChannel).map(([ch, arr]) => [
        ch,
        sortByLabelOrName((arr as IProduct[]).filter(product => !product.hideProductInForecaster)),
      ]),
    );
  },
  {
    condition: (_, { getState }) => {
      const {
        forecaster: { products },
      } = getState();

      return !Object.keys(products || {})?.length;
    },
  },
);

export const getAudience = createAsyncThunk<any, IAbortSignalThunkArg>(
  'forecasterSlice/getAudience',
  async payload => {
    const adswizzAudienceRequest = axios.get<IAdswizzAudienceMetaRecord[]>(ADSWIZZ_AUDIENCES_ENDPOINT, {
      ...payload,
    });

    const beeswaxAudienceRequest = axios.get<IAudienceReferenceRecord[]>(AUDIENCES_ENDPOINT, { ...payload });

    const [beeswaxAudience, adswizzAudience] = await Promise.all([beeswaxAudienceRequest, adswizzAudienceRequest]);

    const audiencesByChannels: IChannelAudience<IAudienceControlOption<string | number>[]> = {
      [Channel.Audio]: [] as IAudienceControlOption<string>[],
      [Channel.SpotifyDirect]: [] as IAudienceControlOption<string>[],
      [Channel.CTV]: [] as IAudienceControlOption<number>[],
      [Channel.Display]: [] as IAudienceControlOption<number>[],
      [Channel.Dooh]: [] as IAudienceControlOption<number>[],
      [Channel.Google]: [] as IAudienceControlOption<number>[],
      [Channel.OLV]: [] as IAudienceControlOption<number>[],
      [Channel.Gaming]: [] as IAudienceControlOption<number>[],
    };

    for (let audience of beeswaxAudience.data) {
      const uniqueChannelNames = [...new Set(audience.channels.map(c => c.name))];
      for (let channel of uniqueChannelNames) {
        if (channel === 'Amazon') continue;
        audiencesByChannels[channel]?.push(AudienceTargetingService.audienceToControlOption(audience));
      }
    }

    const audiencesByChannelWithAudio: IChannelAudience<IAudienceControlOption<string | number>[]> = {
      ...audiencesByChannels,
      [Channel.SpotifyDirect]: AudienceTargetingService.adswizzAudiencesToControlOptions(adswizzAudience.data),
    } as IChannelAudience<IAudienceControlOption<string | number>[]>;

    const sortedByChannel = Object.fromEntries(
      Object.entries(audiencesByChannelWithAudio).map(([ch, arr]) => [ch, sortByLabel(arr as any[])]),
    ) as IChannelAudience<IAudienceControlOption<string | number>[]>;

    return sortedByChannel;
  },
  {
    condition: (_, { getState }) => {
      const {
        forecaster: { audience },
      } = getState() as RootState;

      return !Object.keys(audience || {})?.length;
    },
  },
);

export const getForecasts = createAsyncThunk<
  IPaginatedApiResponse<IForecast[]>,
  IAbortSignalThunkArg | undefined,
  IRejectValueThunkConfig
>(Action.GetForecasts, async (_, { getState, rejectWithValue }) => {
  try {
    const {
      forecaster: {
        filters: { page, limit, search },
      },
    } = getState() as RootState;
    const responseResult = await axios.get<IPaginatedApiResponse<IForecast[]>>(FORECAST_ENDPOINT, {
      params: { page, limit, search },
    });

    return responseResult?.data;
  } catch (err) {
    return rejectWithValue(err as Error);
  }
});

export const deleteForecastTableItem = createAsyncThunk<IForecast[], { id: number }>(
  Action.DeleteForecast,
  async (payload, { getState, rejectWithValue }) => {
    try {
      await axios.delete(`${FORECAST_ENDPOINT}/${payload.id}`);
      const {
        forecaster: { forecasts },
      } = getState() as RootState;
      return forecasts.filter((item: { id: number }) => item.id !== payload.id);
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

// TODO: move out of slice
export const downloadCSV = createAsyncThunk<
  any,
  any,
  {
    state: RootState;
  }
>('forecasterSlice/downloadCSV', async (payload, { rejectWithValue }) => {
  try {
    const response = await axios.post(GET_DOWNLOAD_CSV_ENDPOINT, payload, {
      responseType: 'blob',
    });
    const fileName = `${payload.name}_GeoImpressions_By_${geoTypeLabel(payload.groupBy)}_${new Date().toISOString()}.csv`;

    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    return rejectWithValue(err as Error);
  }
});

export const getForecastGeoImpressions = createAsyncThunk<
  IForecastGeoImpression[],
  any,
  {
    state: RootState;
  }
>('forecasterSlice/getGeoImpressions', async (payload, { rejectWithValue }) => {
  try {
    const response = await axios.post(GET_GEO_IMPRESSIONS, payload);
    return response.data;
  } catch (err) {
    return rejectWithValue(err as Error);
  }
});

export const postForecast = createAsyncThunk<any, any>(
  'forecasterSlice/postForecast',
  async (payload, { rejectWithValue }) => {
    try {
      const responseResult = payload.forecastId
        ? await axios.put(FORECAST_ENDPOINT, {
            ...payload,
          })
        : await axios.post(FORECAST_ENDPOINT, {
            ...payload,
          });
      payload?.onSuccess?.();
      return responseResult.data;
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

export const getGeoOptions = createAsyncThunk<any, any>(
  'forecasterSlice/getGeoOptions',
  async (payload, { rejectWithValue }) => {
    try {
      const responseResult = await axios.get(GEO_TARGETING_SEARCH, {
        params: {
          search: payload,
        },
      });

      return responseResult.data;
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

export const getCurrentForecast = createAsyncThunk<any, any>(
  'forecasterSlice/getCurrentForecast',
  async (payload, { rejectWithValue }) => {
    try {
      if (+payload.id) {
        const responseResult = await axios.get(`${FORECAST_ENDPOINT}/${payload.id}`);
        return mapDataToLoad(responseResult.data[0]);
      }

      return getDefaultFormValues(payload.currentChannel);
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

export const sendAddressRadius = createAsyncThunk<void, ISendAddressRadiusPayload>(
  'forecasterSlice/sendBulkCsv',
  async (payload, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      formData.append('file', payload.file);
      formData.append('channelId', String(CHANNEL_ID_MAP[payload.channel]));
      formData.append('daysInRange', String(payload.daysInRange));

      payload.frequencyCapPerDay && formData.append('frequencyCapPerDay', String(payload.frequencyCapPerDay));
      AudienceTargetingService.checkAudiencesInTargeting(payload.selectedAudience) &&
        formData.append(
          'audience',
          JSON.stringify(AudienceTargetingService.audienceControlOptionsToTargeting(payload.selectedAudience)),
        );
      payload.selectedProduct && formData.append('productId', String(payload.selectedProduct));
      payload.dealGroups?.length &&
        formData.append('dealIds', JSON.stringify(getControlOptionsValues(payload.dealGroups)));

      if (payload.channel === Channel.SpotifyDirect) {
        payload.selectedAdswizzProduct && formData.append('adswizzProductId', String(payload.selectedAdswizzProduct));
        AudienceTargetingService.checkAudiencesInTargeting(payload.selectedAdswizzAudience) &&
          formData.append(
            'adswizzAudience',
            JSON.stringify(AudienceTargetingService.audienceControlOptionsToTargeting(payload.selectedAdswizzAudience)),
          );
      }

      if (payload.channel === Channel.Dooh) {
        payload.doohPublishers?.length &&
          formData.append('doohPublishers', JSON.stringify(payload.doohPublishers.map(p => p.value)));
        payload.venueIds?.length && formData.append('venueIds', JSON.stringify(payload.venueIds.map(v => v.id)));
        payload.mimeTypes && formData.append('mimeTypes', JSON.stringify([payload.mimeTypes]));
      }

      await axios.post(POST_BULK_ADDRESS_CSV_ENDPOINT, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return;
    } catch (err) {
      return rejectWithValue(err as Error);
    }
  },
);

export default forecasterSlice.reducer;
